import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import {
    trackSbViewMoreCtaClick,
    trackRecentItems,
} from 'dibs-recent-history/exports/recentHistoryTracking.ts';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import { trackingConstants, eventNameConstants } from 'dibs-tracking';

import {
    trackRoomModuleClick,
    trackModuleClick,
} from '../../utils/tracking/searchBrowse/nullResultsTracking.ts';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

// components
import { SbRespNullSearchResultsTiles } from './SbRespNullSearchResultsTiles.tsx';
import { CategoryRooms } from 'dibs-buyer-layout/exports/CategoryLinks.ts';
import { SbRespFollowSearchBannerLazy } from '../SbRespFollowSearchBanner/SbRespFollowSearchBannerLazy.tsx';
import { SharedRecentlyFavoritedItems } from '../../shared/SharedRecentlyFavoritedItems/SharedRecentlyFavoritedItems.tsx';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy.ts';
import SharedYMAL from '../../shared/SharedYMAL/SharedYMAL.tsx';
import { SharedFollowTopButton } from '../../shared/SharedFollowTopButton/SharedFollowTopButton.tsx';
import { SharedRecentSales } from '../../shared/SharedRecentSales/SharedRecentSales.tsx';
import { SbSharedRecommendedItems } from '../SbSharedRecommendedItems/SbSharedRecommendedItems.tsx';
import { SbSharedControlledHeadingWrapper } from '../SbSharedControlledHeadingWrapper/SbSharedControlledHeadingWrapper.tsx';
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle.tsx';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs.tsx';

import dibsCss from 'dibs-css';
import classnames from 'classnames';

const onRoomModuleClick = (e, { room }) => trackRoomModuleClick(room.type);

const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;
const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST } = eventNameConstants;

const handleCarouselItemClick = ({ item, index, actionLabel }) => {
    trackModuleClick(actionLabel, index);
    trackRecentItems({
        type: ECOM_PRODUCT_CLICK,
        items: [item],
        startIndex: index + 1, // Tracking needs non zero index position.
        variant: 'null results page',
        list: `null results ${actionLabel} carousel`,
        eventName: EVENT_SELECT_ITEM,
    });
};

const handleCarouselItemImpression = ({ items, index, actionLabel }) => {
    trackRecentItems({
        type: ECOM_PRODUCT_IMPRESSION,
        items: items,
        startIndex: index + 1, // Tracking needs non zero index position.
        variant: 'null results page',
        list: `null results ${actionLabel} carousel`,
        eventName: EVENT_VIEW_ITEM_LIST,
    });
};

// SEARCH-2608 - get around the knip rules to temporarily disable recently viewed items
const shouldRenderRecentlyViewedItems = false;

const SbRespNullSearchResultsComponent = props => {
    const { user, viewer, itemSearch, isClient } = props;
    const showNullPageModules = itemSearch?.soldItems?.totalResults;
    const { currency } = useCurrency();
    const { isBot } = useServerVarsContext();

    return (
        <>
            <SbSharedPageBreadcrumbs itemSearch={itemSearch} user={user} />
            <SbSharedControlledHeadingWrapper itemSearch={itemSearch}>
                {Heading => (
                    <>
                        <SbSharedTitle itemSearch={itemSearch} user={user} Heading={Heading}>
                            <SharedFollowTopButton
                                itemSearch={itemSearch}
                                user={user}
                                addLeadingSpace
                            />
                        </SbSharedTitle>
                        {showNullPageModules ? (
                            <div
                                className={classnames(
                                    dibsCss.flex,
                                    dibsCss.flexCol,
                                    dibsCss.wFull,
                                    dibsCss.minW0,
                                    dibsCss.pySmall,
                                    dibsCss.px0
                                )}
                            >
                                <SharedRecentSales
                                    viewer={viewer}
                                    itemSearch={itemSearch}
                                    user={user}
                                />
                                <SharedYMAL viewer={viewer} user={user} itemSearch={itemSearch} />
                                <SbSharedRecommendedItems viewer={viewer} />
                            </div>
                        ) : (
                            <>
                                {isClient && (
                                    <SbRespFollowSearchBannerLazy
                                        itemSearch={itemSearch}
                                        user={user}
                                    />
                                )}
                                <SbRespNullSearchResultsTiles />
                            </>
                        )}
                        <RecentlyViewedItemsLazy
                            wrapperClassName={classnames(dibsCss.mxXsmall, dibsCss.mtMedium)}
                            authModalLoader={authModalLoader}
                            trackItemClick={handleCarouselItemClick}
                            trackItemImpression={handleCarouselItemImpression}
                            currency={currency}
                            hasTopSeparator
                            isBot={isBot}
                        />
                        {!showNullPageModules && (
                            <>
                                {shouldRenderRecentlyViewedItems && (
                                    <SharedRecentlyFavoritedItems
                                        onViewMoreCtaClick={trackSbViewMoreCtaClick}
                                        onItemClick={handleCarouselItemClick}
                                        onItemImpression={handleCarouselItemImpression}
                                    />
                                )}
                                <CategoryRooms onClick={onRoomModuleClick} />
                            </>
                        )}
                    </>
                )}
            </SbSharedControlledHeadingWrapper>
        </>
    );
};

SbRespNullSearchResultsComponent.propTypes = {
    user: PropTypes.object,
    viewer: PropTypes.object,
    isClient: PropTypes.bool,
    searchTerm: PropTypes.string,
    relay: PropTypes.object.isRequired,
    itemSearch: PropTypes.object.isRequired,
};

export const SbRespNullSearchResults = createFragmentContainer(SbRespNullSearchResultsComponent, {
    user: graphql`
        fragment SbRespNullSearchResults_user on User {
            ...SbRespFollowSearchBanner_user
            ...SharedYMAL_user
            ...SharedFollowTopButton_user
            ...SbSharedTitle_user
            ...SharedRecentSales_user
            ...SbSharedPageBreadcrumbs_user
        }
    `,
    viewer: graphql`
        fragment SbRespNullSearchResults_viewer on Viewer {
            ...SbSharedRecommendedItems_viewer
            ...SharedRecentSales_viewer
            ...SharedYMAL_viewer
        }
    `,
    itemSearch: graphql`
        fragment SbRespNullSearchResults_itemSearch on ItemSearchQueryConnection {
            ...SbSharedPageBreadcrumbs_itemSearch
            ...SbRespFollowSearchBanner_itemSearch
            ...SharedFollowSearchBanner_itemSearch
            ...SharedYMAL_itemSearch
            ...SharedFollowTopButton_itemSearch
            soldItems: soldResults(first: 28) {
                totalResults
            }
            ...SharedRecentSales_itemSearch
            ...SbSharedTitle_itemSearch
            ...SbSharedControlledHeadingWrapper_itemSearch
        }
    `,
});
